import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Select,
    MenuItem,
    Typography,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { axios_tenant } from "../../../../axios";
import { toast } from "react-toastify";
import AddOAUserDialog from "./AddOAUserDialog";
import Button from "./atoms/Button";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "../../../pages/CollaborationSpace/OAWorkflow/OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";

const ManageUserModal = ({ open, onClose, onUsersChanged, workType = "OA Shell" }) => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);   // fetched from /oashell-roles
    const [loading, setLoading] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editRoleId, setEditRoleId] = useState("");

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios_tenant.get(`${BASE_URL}/oashell-users`, {
                params: { workType }
            });
            setUsers(res.data?.data || []);
        } catch (err) {
            console.error(`Failed to fetch ${workType} users:`, err);
            toast.error(`Failed to load ${workType} users`);
        } finally {
            setLoading(false);
        }
    }, [workType]);

    const fetchRoles = useCallback(async () => {
        try {
            const res = await axios_tenant.get(`${BASE_URL}/oashell-roles`, {
                params: { workType }
            });
            setRoles(res.data?.data || []);
        } catch (err) {
            console.error(`Failed to fetch ${workType} roles:`, err);
        }
    }, [workType]);

    useEffect(() => {
        if (open) {
            fetchUsers();
            fetchRoles();
        }
    }, [open, fetchUsers, fetchRoles]);

    const handleStartEdit = (user) => {
        setEditingId(user.id);
        setEditRoleId(user.roleId);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditRoleId("");
    };

    const handleSaveEdit = async (userId) => {
        try {
            await axios_tenant.put(`${BASE_URL}/oashell-users/${userId}`, { roleId: editRoleId, workType });
            toast.success("User role updated");
            setEditingId(null);
            fetchUsers();
            onUsersChanged?.();
        } catch (err) {
            toast.error("Failed to update user");
        }
    };

    const handleToggleStatus = async (user) => {
        try {
            const newStatus = user.oaStatus === "deactivated" ? "active" : "deactivated";
            await axios_tenant.put(`${BASE_URL}/oashell-users/${user.id}`, { status: newStatus, workType });
            toast.success(`User ${newStatus === "active" ? "activated" : "deactivated"}`);
            fetchUsers();
            onUsersChanged?.();
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    const handleUserAdded = () => {
        setAddDialogOpen(false);
        fetchUsers();
        onUsersChanged?.();
    };

    const getRoleColor = (roleName) => {
        switch (roleName) {
            case "Application Manager": return "primary";
            case "Processor": return "success";
            case "QA": return "secondary";
            default: return "default";
        }
    };

    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    return (
        <ThemeProvider theme={localTheme}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ className: "oa-lato-font", sx: { borderRadius: 2, minHeight: 400 } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Typography variant="h6" fontWeight={600}>{workType} Users</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="primary"
                            onClick={() => setAddDialogOpen(true)}
                        >
                            + Add User
                        </Button>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">No {workType} users found. Click "+ Add User" to get started.</Typography>
                        </Box>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell sx={{ fontWeight: 600 }}>User Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>OA Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow key={user.id} hover>
                                            <TableCell>{`${user.firstName || ""} ${user.lastName || ""}`.trim() || "—"}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                {editingId === user.id ? (
                                                    <Select
                                                        value={editRoleId}
                                                        onChange={(e) => setEditRoleId(e.target.value)}
                                                        size="small"
                                                        sx={{ minWidth: 160 }}
                                                    >
                                                        {roles.filter(r => r.roleName !== "Admin").map(r => (
                                                            <MenuItem key={r.id} value={r.id}>{r.roleName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Chip
                                                        label={user.roleName}
                                                        color={getRoleColor(user.roleName)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.oaStatus !== "deactivated" ? "Active" : "Deactivated"}
                                                    color={user.oaStatus !== "deactivated" ? "success" : "default"}
                                                    size="small"
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                    {editingId === user.id ? (
                                                        <>
                                                            <IconButton size="small" onClick={() => handleSaveEdit(user.id)} color="primary">
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={handleCancelEdit} color="default">
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <IconButton size="small" onClick={() => handleStartEdit(user)} color="primary">
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <Button
                                                                variant={user.oaStatus === "deactivated" ? "primary" : "secondary"}
                                                                onClick={() => handleToggleStatus(user)}
                                                                sx={{ 
                                                                    minWidth: 100, 
                                                                    py: 0.5, 
                                                                    fontSize: '12px',
                                                                    height: '30px',
                                                                    backgroundColor: user.oaStatus === "deactivated" ? '#1a1a2e' : 'transparent',
                                                                    color: user.oaStatus === "deactivated" ? '#fff' : '#1a1a2e'
                                                                }}
                                                            >
                                                                {user.oaStatus === "deactivated" ? "Activate" : "Deactivate"}
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>

            <AddOAUserDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onUserAdded={handleUserAdded}
                roles={roles}
                oaUsers={users}
                workType={workType}
            />
        </ThemeProvider>
    );
};

export default ManageUserModal;
